import React, { useEffect, useRef, useState } from "react";
import { EditorState } from "draft-js";
import { marked } from "marked";
import { stateFromHTML } from "draft-js-import-html";
import { stateToMarkdown } from "draft-js-export-markdown";
import EditorBox from "features/Bookmarks/components/Editor";
import { Box, Checkbox, Divider, Stack, Typography } from "@mui/material";
import { getRegularDate } from "utils/time.util";

export default function TeacherNotesView({
  submission,
  readOnly = true,
  onReviewedChange,
  onSave,
}) {
  const [editorState, setEditorState] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const changingTimeout = useRef(null);
  const loadedSubmissionId = useRef(null);
  const isInitializing = useRef(false);

  const notes = submission?.teacherNote_chr || submission?.teacherNote || "";

  const markdownToContentState = (markdown) =>
    stateFromHTML(marked(markdown || ""));

  const getMarkdown = (state) =>
    stateToMarkdown(state.getCurrentContent());

  // Only reload the editor when a different submission is opened. Saving updates the
  // submission object, and rebuilding the content on every save resets the caret.
  useEffect(() => {
    const submissionId = submission?.submissionID_ids ?? null;
    if (submissionId == null || submissionId === loadedSubmissionId.current) return;
    loadedSubmissionId.current = submissionId;
    isInitializing.current = true;
    setEditorState(EditorState.createWithContent(markdownToContentState(notes)));
    setIsDirty(false);
  }, [submission, notes]);

  useEffect(() => () => clearTimeout(changingTimeout.current), []);

  const saveNotes = async () => {
    if (readOnly || !editorState || !onSave) return;
    setIsSaving(true);
    setIsDirty(false);
    try {
      await onSave(getMarkdown(editorState));
    } catch (error) {
      setIsDirty(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = () => {
    // Loading a submission into the editor counts as a content change; it is not an edit.
    if (isInitializing.current) {
      isInitializing.current = false;
      return;
    }
    setIsDirty(true);
    if (changingTimeout.current) clearTimeout(changingTimeout.current);
    changingTimeout.current = setTimeout(saveNotes, 1500);
  };

  if (!submission) {
    return <Typography color="text.secondary">No submission details are available.</Typography>;
  }

  return (
    <Box sx={{ mt: 2, mb: 8 }}>
      {/* One meta line: the items keep their own text intact and wrap as a group when
          the column is too narrow, instead of each one wrapping mid-label. */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        columnGap={4}
        rowGap={1}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ "& > p": { whiteSpace: "nowrap" } }}
      >
        <Typography>Folder: <strong>{submission.folderName_chr || "—"}</strong></Typography>
        <Typography>Submitted: <strong>{getRegularDate(submission.submittedDateTime_dtm)}</strong></Typography>
        {submission.lastUpdatedDateTime_dtm && submission.lastUpdatedDateTime_dtm !== submission.submittedDateTime_dtm && (
          <Typography>Updated: <strong>{getRegularDate(submission.lastUpdatedDateTime_dtm)}</strong></Typography>
        )}
        <Stack direction="row" alignItems="center" sx={{ whiteSpace: "nowrap" }}>
          <Typography>Reviewed</Typography>
          <Checkbox
            checked={Boolean(submission.isReviewed_ysn)}
            disabled={readOnly}
            onChange={(event) => onReviewedChange?.(event.target.checked)}
          />
        </Stack>
      </Stack>
      <Typography variant="h4" mt={5} mb={1}>Teacher&apos;s Notes</Typography>
      <Typography color="text.secondary" mb={2}>
        (These notes are visible to students as read-only)
      </Typography>
      {editorState && (
        <EditorBox
          summaryEditor
          isFocused
          setIsFocused={() => {}}
          editorState={editorState}
          setEditorState={setEditorState}
          handleSave={() => {}}
          handleBlur={() => {}}
          fromSummary
          isBlur
          onEnterSave={false}
          shouldSave={isDirty}
          showSaveIcon
          placeholder="Add feedback for the student"
          isSaving={isSaving}
          handleContentChange={handleContentChange}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
}
